import { useState, useEffect, useRef, useMemo } from 'react';
import Api from 'entities/helpers/api';

/**
 * Transforms philanthropyMatches to charitable table format.
 */
const transformCharitable = (matches = []) =>
  matches.map((match, idx) => ({
    id: idx,
    listed_donor: match.donorName || '—',
    recipient: match.organizationName || '—',
    recipient_url: match.orgHomePage || null,
    recipient_location: [match.orgCity, match.state].filter(Boolean).join(', ') || '—',
    gift_type: match.giftType || '—',
    gift_type_url: match.linkToSource || null,
    year: match.giftYear || '—',
    low_amount: match.lowGiftLevel,
    high_amount: match.highGiftLevel,
    confidence_score: match.qualityScore,
  }));

/**
 * Transforms fecMatches + stateMatches to political table format.
 */
const transformPolitical = (fecMatches = [], stateMatches = []) => {
  const allMatches = [...fecMatches, ...stateMatches];
  return allMatches.map((match, idx) => ({
    id: idx,
    listed_donor: match.donorName || '—',
    recipient: match.organizationName || '—',
    donor_location: [match.donorCity, match.donorState].filter(Boolean).join(', ') || '—',
    gift_type: match.giftType || 'FEC',
    gift_type_url: match.linkToSource || null,
    year: match.giftYear || '—',
    low_amount: match.lowGiftLevel,
    high_amount: match.highGiftLevel,
    category: match.orgCategory || '—',
    confidence_score: match.qualityScore,
  }));
};

/**
 * Transforms crowdfundingMatches to crowdfunding table format.
 */
const transformCrowdfunding = (matches = []) =>
  matches.map((match, idx) => ({
    id: idx,
    listed_donor: match.fullName || '—',
    recipient: match.campaignName || '—',
    recipient_url: match.campaignUrl || null,
    donor_location: [match.donorCity, match.donorState].filter(Boolean).join(', ') || '—',
    year: match.date ? new Date(match.date).getFullYear() : '—',
    low_amount: match.amount,
    high_amount: match.amount,
    confidence_score: match.qualityScore,
  }));

/**
 * Transforms realEstateMatches to real estate table format.
 */
const GOOGLE_MAPS_KEY = 'AIzaSyDGNa0vJIdeMUYAsIpnFAPbZefWJ8RlVhY';

/**
 * Builds a Google Street View thumbnail URL from address fields.
 */
const buildPropertyThumbnailUrl = (match) => {
  const street = match.address1 || match.propertyFullStreet;
  const city = match.city || match.propertyCityName;
  const state = match.state || match.propertyState;
  const zip = match.zip || match.propertyZip;
  const location = [street, city, state, zip].filter(Boolean).join(', ');
  if (!location) return null;
  const params = new URLSearchParams({ size: '160x110', location, key: GOOGLE_MAPS_KEY });
  return `https://maps.googleapis.com/maps/api/streetview?${params}`;
};

const transformRealEstate = (matches = []) =>
  matches.map((match, idx) => {
    const streetAddress = match.address1 || match.propertyFullStreet || '';
    const cityStateZip = [match.city, match.state, match.zip].filter(Boolean).join(', ');

    return {
      id: match.matchingId || idx,
      name: match.assesseeOwnerName || match.currentOwnerName || '—',
      address_street: streetAddress || '—',
      address_city_state: cityStateZip || '—',
      thumbnail_url: buildPropertyThumbnailUrl(match),
      property_type: match.landType || match.propertyUnitType || '—',
      estimated_value: match.totalAssessedValue || match.avm || match.latestValidPrice,
      confidence_score: match.qualityScore ? parseFloat(match.qualityScore) : null,
    };
  });

/** Strips commas and parses a value as a number, returning 0 for non-numeric input. */
const toNum = (v) => parseFloat(String(v ?? '').replace(/,/g, '')) || 0;

/**
 * Transforms secMatches to SEC holdings table format.
 * Each secMatch contains a ciks array — each CIK is one row.
 * Holdings within a CIK are aggregated into a single row.
 */
const transformSec = (matches = []) => {
  const rows = [];
  matches.forEach((match, matchIdx) => {
    const ciks = match.ciks || [];
    ciks.forEach((cik, cikIdx) => {
      const holdings = cik.holdings || [];

      // Aggregate shares and market value across all holdings in this CIK
      let totalShares = 0;
      let totalMarketValue = 0;
      holdings.forEach((entry) => {
        const h = entry.holding || entry;
        totalShares += toNum(h.total ?? h.direct);
        totalMarketValue += toNum(h.marketValue ?? h.currentValue);
      });

      // Company name from first regulatoryCriteria key, or first holding symbol
      const companyName = cik.regulatoryCriteria
        ? Object.keys(cik.regulatoryCriteria)[0]?.trim() || '—'
        : holdings[0]?.holding?.symbol || holdings[0]?.symbol || cik.symbol || '—';

      rows.push({
        id: `${matchIdx}-${cikIdx}`,
        name: cik.name || match.name || '—',
        company: companyName,
        total_shares_held: totalShares || null,
        market_value: totalMarketValue || null,
        total_shares_sold: cik.totalSold || null,
        confidence_score: match.qualityScore,
      });
    });
  });
  return rows;
};

// DonorSearch qualityScore range is 0–25.
// 18+: High Quality, 17–17.9: Maybe (shown as high), 0–16.9: Low Quality
const HIGH_CONFIDENCE_THRESHOLD = 17;

/**
 * Splits rows into high and low confidence arrays based on qualityScore threshold.
 * Rows with null/undefined scores are placed in the low confidence bucket.
 */
const splitByConfidence = (rows) => {
  const high = [];
  const low = [];
  for (const row of rows) {
    if (row.confidence_score != null && row.confidence_score >= HIGH_CONFIDENCE_THRESHOLD) {
      high.push(row);
    } else {
      low.push(row);
    }
  }
  return { high, low };
};

/**
 * Transforms API response data based on the category.
 */
export const transformData = (response, category) => {
  if (!response) return { high: [], low: [] };

  let rows;
  switch (category) {
    case 'charitable':
      rows = transformCharitable(response.philanthropyMatches);
      break;
    case 'political':
      rows = transformPolitical(response.fecMatches, response.stateMatches);
      break;
    case 'crowdfunding':
      rows = transformCrowdfunding(response.crowdfundingMatches);
      break;
    case 'realEstate':
      rows = transformRealEstate(response.realEstateMatches);
      break;
    case 'sec':
      rows = transformSec(response.secMatches);
      break;
    default:
      return { high: [], low: [] };
  }
  return splitByConfidence(rows);
};

/**
 * Fetches DonorSearch table data for a contact.
 * Caches the raw API response and derives tab-specific data client-side.
 */
const useDonorSearchTable = (contactId, donorsearchProfileId, category) => {
  const [rawResponse, setRawResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cacheKey = useRef(null);

  useEffect(() => {
    const key = `${contactId}-${donorsearchProfileId}`;
    if (!contactId || !donorsearchProfileId) {
      setRawResponse(null);
      setLoading(false);
      cacheKey.current = null;
      return;
    }

    // Skip fetch if we already have data for this contact/profile
    if (cacheKey.current === key) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    Api.ENRICHMENT.TABLE.get({
      params: { donorsearch_profile_id: donorsearchProfileId, is_detail: true },
      success: (response) => {
        if (cancelled) return;
        cacheKey.current = key;
        setRawResponse(response);
        setLoading(false);
      },
      error: (xhr, status, err) => {
        if (cancelled) return;
        setError(err || 'Failed to fetch table data');
        setRawResponse(null);
        cacheKey.current = null;
        setLoading(false);
      },
    });

    return () => {
      cancelled = true;
    };
  }, [contactId, donorsearchProfileId]);

  const data = useMemo(() => transformData(rawResponse, category), [rawResponse, category]);

  const summary = useMemo(() => {
    if (!rawResponse) return null;

    // TEMPORARY: Derive real estate counts from table matches (source of truth)
    // until DonorSearch fixes the /summary quality count endpoint.
    const realEstateSplit = splitByConfidence(transformRealEstate(rawResponse.realEstateMatches));
    const realEstateHighAmount = realEstateSplit.high.reduce((sum, r) => sum + toNum(r.estimated_value), 0);

    return {
      estimatedCapacity: rawResponse.estimatedCapacity ?? null,
      largestGiftLow: rawResponse.largestGiftLow ?? null,
      realEstateEst: rawResponse.realEstateEst ?? null,
      realEstateCount: rawResponse.realEstateCount ?? null,
      // TEMPORARY: table-derived counts override summary counts for real estate
      realEstateFromTable: {
        amount: realEstateHighAmount ?? null,
        highCount: realEstateSplit.high.length,
        lowCount: realEstateSplit.low.length,
      },
    };
  }, [rawResponse]);

  return { data, rawResponse, summary, loading, error };
};

export default useDonorSearchTable;
